import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatLocalDateTime, formatLocalTime, parseServerDate } from '../../utils/timeUtils';
import { flagUrl } from '../../i18n/languages';
import './steamStatus.css';

const REFRESH_S = 60;

// Valve's datacenter names → region group, flag, airport-style code, city (same layout as steamstat.us).
// A name Valve adds later still shows, under "Other", with its own name.
const DC = {
  'EU Frankfurt': ['eu', 'de', 'FRA', 'Frankfurt'], 'EU Falkenstein': ['eu', 'de', 'FSN', 'Falkenstein'],
  'EU Helsinki': ['eu', 'fi', 'HEL', 'Helsinki'], 'United Kingdom': ['eu', 'gb', 'LHR', 'London'],
  'EU Madrid': ['eu', 'es', 'MAD', 'Madrid'], 'EU Stockholm': ['eu', 'se', 'STO', 'Stockholm'],
  'EU Vienna': ['eu', 'at', 'VIE', 'Vienna'], 'EU Warsaw': ['eu', 'pl', 'WAW', 'Warsaw'],
  'US Atlanta': ['am', 'us', 'ATL', 'Atlanta'], 'US Chicago': ['am', 'us', 'ORD', 'Chicago'],
  'US Dallas': ['am', 'us', 'DFW', 'Dallas'], 'US California': ['am', 'us', 'LAX', 'Los Angeles'],
  'US Seattle': ['am', 'us', 'SEA', 'Seattle'], 'US Virginia': ['am', 'us', 'IAD', 'Sterling'],
  'Argentina': ['am', 'ar', 'EZE', 'Buenos Aires'], 'Peru': ['am', 'pe', 'LIM', 'Lima'],
  'Chile': ['am', 'cl', 'SCL', 'Santiago'], 'Brazil': ['am', 'br', 'GRU', 'São Paulo'],
  'India Chennai': ['as', 'in', 'MAA', 'Chennai'], 'India Mumbai': ['as', 'in', 'BOM', 'Mumbai'],
  'Emirates': ['as', 'ae', 'DXB', 'Dubai'], 'Hong Kong': ['as', 'hk', 'HKG', 'Hong Kong'],
  'South Korea': ['as', 'kr', 'SEO', 'Seoul'], 'Singapore': ['as', 'sg', 'SGP', 'Singapore'],
  'Japan': ['as', 'jp', 'TYO', 'Tokyo'], 'China Beijing': ['as', 'cn', 'PEK', 'Beijing'],
  'China Chengdu': ['as', 'cn', 'CTU', 'Chengdu'], 'China Guangdong': ['as', 'cn', 'CAN', 'Guangdong'],
  'China Pudong': ['as', 'cn', 'SHA', 'Shanghai'],
  'Australia': ['ot', 'au', 'SYD', 'Sydney'], 'South Africa': ['ot', 'za', 'JNB', 'Johannesburg'],
};
const DC_GROUPS = ['eu', 'am', 'as', 'ot'];

// Status log by day (viewer's local date): newest day first, events newest first within a day.
const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function logDays(events) {
  const days = new Map();
  for (const ev of events) {
    const d = parseServerDate(ev.at);
    if (!d) continue;
    const key = localDayKey(d);
    if (!days.has(key)) days.set(key, { key, date: d, events: [] });
    days.get(key).events.push(ev);
  }
  return [...days.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
}

// Sidebar dot + page: one poll every 60 s while the tab is visible (the server caches 60 s too).
// reload(true) = the Refresh button: the server asks Valve again if its answer is older than 10 s.
export function useSteamStatus() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nextAt, setNextAt] = useState(0);
  const timer = useRef(null);
  const nextRef = useRef(0);
  const load = useCallback(async (force = false) => {
    clearTimeout(timer.current);
    setLoading(true);
    try {
      const r = await axios.get('/api/steam-status', { timeout: 15000, params: force ? { refresh: 1 } : undefined });
      setData(r.data); setError(false);
    } catch (_) { setError(true); }
    finally {
      setLoading(false);
      nextRef.current = Date.now() + REFRESH_S * 1000;
      setNextAt(nextRef.current);
      // a hidden tab skips the poll; becoming visible catches up (below)
      timer.current = setTimeout(function tick() {
        if (document.hidden) { timer.current = setTimeout(tick, 5000); return; }
        load();
      }, REFRESH_S * 1000);
    }
  }, []);
  useEffect(() => {
    load();
    // back on the tab: load only if the minute is up (switching tabs must not reset the timer)
    const onVisible = () => { if (!document.hidden && Date.now() >= nextRef.current) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(timer.current); document.removeEventListener('visibilitychange', onVisible); };
  }, [load]);
  return { data, error, loading, nextAt, reload: load };
}

function useCountdown(nextAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return Math.max(0, Math.ceil((nextAt - now) / 1000));
}

export default function SteamStatus() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, error, loading, nextAt, reload } = useSteamStatus();
  const secs = useCountdown(nextAt);
  const isSuper = user?.role === 'super_admin' || user?.role === 'admin';
  // Status log: today open (until closed), older days collapsed until clicked
  const [openDays, setOpenDays] = useState(() => new Set());
  const [closedToday, setClosedToday] = useState(false);
  const todayKey = localDayKey(new Date());
  const yesterdayKey = localDayKey(new Date(Date.now() - 864e5));
  const dayLabel = (day) => (day.key === todayKey ? t('steamStatus.today', 'Today')
    : day.key === yesterdayKey ? t('steamStatus.yesterday', 'Yesterday')
      : day.date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }));

  const CARDS = {
    api: { title: t('steamStatus.api', 'CS2 API'), text: t('steamStatus.apiText', "Valve's Counter-Strike web API answers.") },
    logon: { title: t('steamStatus.logon', 'Sessions Logon'), text: t('steamStatus.logonText', 'Steam sign-in for CS2. When it is down, players stay STEAM_ID_PENDING and may not be able to join servers.') },
    inventories: { title: t('steamStatus.inventories', 'Player Inventories'), text: t('steamStatus.inventoriesText2', 'Items, skins and the Steam Community. Down = inventories may not load in game.') },
    scheduler: { title: t('steamStatus.scheduler', 'Matchmaking Scheduler'), text: t('steamStatus.schedulerText', "Valve's matchmaking. Does not affect panel matches on your own servers.") },
  };
  const LABEL = {
    good: t('steamStatus.good', 'Normal'),
    minor: t('steamStatus.minor', 'Degraded'),
    major: t('steamStatus.major', 'Down'),
    unknown: t('steamStatus.unknown', 'Unknown'),
  };

  const DC_STATUS = { good: t('steamStatus.dcOk', 'OK'), minor: t('steamStatus.dcLimited', 'Limited'), major: t('steamStatus.dcDown', 'Down'), unknown: t('steamStatus.unknown', 'Unknown') };
  const LOAD = {
    idle: t('steamStatus.loadIdle', 'Idle'), low: t('steamStatus.loadLow', 'Low load'), medium: t('steamStatus.loadMedium', 'Medium load'),
    high: t('steamStatus.loadHigh', 'High load'), full: t('steamStatus.loadFull', 'Full load'),
  };
  const GROUP = { eu: t('steamStatus.europe', 'Europe'), am: t('steamStatus.americas', 'Americas'), as: t('steamStatus.asia', 'Asia'), ot: t('steamStatus.other', 'Other') };

  const items = data?.items || [];
  const bad = items.filter(i => i.status === 'major' || i.status === 'minor');
  const overall = data?.overall || 'unknown';
  const dcs = (data?.datacenters || []).map(d => {
    const m = DC[d.name];
    return { ...d, group: m ? m[0] : 'ot', flag: m ? m[1] : null, code: m ? m[2] : '', city: m ? m[3] : d.name };
  });
  const dcDown = dcs.filter(d => d.status === 'major' || d.status === 'minor').length;

  return (
    <div className="ss-layout">
    <div className="ss-page">
      <div className="ss-head">
        <div>
          <h1 className="ss-title">{t('nav.steamStatus', 'Steam Status')}</h1>
          <p className="ss-sub">{t('steamStatus.subtitle', "Is CS2 or Steam sign-in down? Checked against Valve's own status every minute.")}</p>
        </div>
        <button type="button" className="ss-refresh" onClick={() => reload(true)} disabled={loading}>
          <svg className={loading ? 'spin' : ''} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>
          {loading ? t('steamStatus.refreshing', 'Refreshing…') : t('steamStatus.refresh', 'Refresh')}
          {!loading && nextAt > 0 && <span className="ss-count">{secs}s</span>}
        </button>
      </div>

      {error && !data && <div className="ss-banner major">{t('steamStatus.loadFailed', 'Could not load the status from the panel.')}</div>}

      {data?.error === 'no_key' && (
        <div className="ss-banner unknown">
          <b>{t('steamStatus.noKeyTitle', 'A Steam Web API key is needed')}</b>
          <span>{t('steamStatus.noKeyText', "Valve's status API only answers with a key.")}{' '}
            {isSuper
              ? <Link to="/admin/settings/integrations">{t('steamStatus.addKey', 'Add it in Settings → Integrations')}</Link>
              : t('steamStatus.askSuper', 'Ask a super admin to add it in Settings.')}
          </span>
        </div>
      )}
      {data?.error === 'key_refused' && (
        <div className="ss-banner unknown">
          <b>{t('steamStatus.keyRefusedTitle', 'Valve refused the Steam Web API key')}</b>
          <span>{isSuper
            ? <Link to="/admin/settings/integrations">{t('steamStatus.checkKey', 'Check the key in Settings → Integrations')}</Link>
            : t('steamStatus.askSuper', 'Ask a super admin to add it in Settings.')}</span>
        </div>
      )}
      {data && !data.error && (
        <div className={`ss-banner ${overall}`} role="status">
          <b>{overall === 'good'
            ? t('steamStatus.allGood', 'CS2 and Steam sign-in are working')
            : t('steamStatus.problem', 'Problem: {{list}}', { list: bad.map(i => CARDS[i.key].title).join(', ') })}</b>
        </div>
      )}
      {data && data.error && data.error !== 'no_key' && data.error !== 'key_refused' && (
        <div className="ss-banner major" role="status">
          <b>{t('steamStatus.apiDown', "Valve's CS2 API is not answering ({{e}}) - CS2 / Steam may be down", { e: data.error })}</b>
        </div>
      )}

      {!data && !error && <div className="ss-muted">{t('common.loading', 'Loading...')}</div>}

      {items.length > 0 && (
        <div className="ss-grid">
          {items.map(i => (
            <div key={i.key} className={`ss-card ${i.status}`}>
              <div className="ss-card-top">
                <span className="ss-card-title">{CARDS[i.key]?.title || i.key}</span>
                <span className={`ss-pill ${i.status}`}>{LABEL[i.status] || i.status}</span>
              </div>
              <p className="ss-card-text">{CARDS[i.key]?.text}</p>
              <div className="ss-card-foot">
                {i.value && <span className="ss-raw" title={t('steamStatus.valveSays', 'What Valve reports')}>{i.value}</span>}
                {i.status !== 'unknown' && i.since && <span>{t('steamStatus.since', 'since {{time}}', { time: formatLocalDateTime(i.since) })}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {dcs.length > 0 && (
        <div className="ss-dc">
          <div className="ss-dc-head">
            <h2>{t('steamStatus.servers', 'Valve servers (CS2)')}</h2>
            <span className={dcDown ? 'ss-dc-sum bad' : 'ss-dc-sum'}>
              {dcDown
                ? t('steamStatus.dcProblems', '{{n}} of {{total}} regions with problems', { n: dcDown, total: dcs.length })
                : t('steamStatus.dcAllOk', 'All {{total}} regions OK', { total: dcs.length })}
            </span>
          </div>
          <div className="ss-dc-groups">
            {DC_GROUPS.map(g => {
              const list = dcs.filter(d => d.group === g).sort((a, b) => a.city.localeCompare(b.city));
              if (!list.length) return null;
              return (
                <div key={g} className="ss-dc-group">
                  <div className="ss-dc-gname">{GROUP[g]}</div>
                  {list.map(d => (
                    <div key={d.name} className="ss-dc-row" title={`${d.name}, capacity: ${d.capacity || '-'}, load: ${d.load || '-'}`}>
                      {d.flag ? <img src={flagUrl(d.flag, 40)} alt="" className="ss-flag" loading="lazy" /> : <span className="ss-flag" />}
                      <span className="ss-code">{d.code}</span>
                      <span className="ss-city">{d.city}</span>
                      <span className={`ss-dc-st ${d.status}`}>{DC_STATUS[d.status]}</span>
                      <span className="ss-load">{LOAD[String(d.load || '').toLowerCase()] || d.load || ''}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <div className="ss-muted" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.25rem' }}>
          <span>{t('steamStatus.checked', 'Last checked {{time}} · refreshes every minute', { time: formatLocalTime(data.checked_at) })}</span>
          <span>{t('steamStatus.source', 'Source: Valve GetGameServersStatus')}</span>
        </div>
      )}
    </div>

    {/* Status log: every change of a Valve service — CS2 API, sessions logon, inventories, matchmaking
        (server-side, kept 3 days). Valve's server regions are shown live above but not logged. */}
    <aside className="ss-log">
      <div className="ss-log-head">
        <h2>{t('steamStatus.logTitle', 'Status log')}</h2>
        <span>{t('steamStatus.logSub', 'Problems and recoveries, last 3 days')}</span>
      </div>
      {data && (data.events || []).length === 0 && (
        <div className="ss-log-empty">
          <span className="ss-log-ok">✓</span>
          {t('steamStatus.logEmpty', 'Nothing has gone wrong in the last 3 days.')}
        </div>
      )}
      <div className="ss-log-list">
        {logDays(data?.events || []).map(day => {
          const open = openDays.has(day.key) || (day.key === todayKey && !closedToday);
          const problems = day.events.filter(ev => ev.to !== 'good').length;
          const toggle = () => {
            if (day.key === todayKey) { setClosedToday(open); return; }
            setOpenDays(prev => { const n = new Set(prev); if (n.has(day.key)) n.delete(day.key); else n.add(day.key); return n; });
          };
          return (
            <div key={day.key} className={`ss-log-day${open ? ' open' : ''}`}>
              <button type="button" className="ss-log-dayhead" onClick={toggle} aria-expanded={open}>
                <span className="ss-log-chev">▸</span>
                <span className="ss-log-date">{dayLabel(day)}</span>
                <span className="ss-log-count">{t('steamStatus.logChanges', '{{count}} changes', { count: day.events.length })}</span>
                {problems > 0 && <span className="ss-log-bad">{t('steamStatus.logProblems', '{{count}} problems', { count: problems })}</span>}
              </button>
              {open && day.events.map(ev => {
                const isService = ev.kind === 'service';
                const m = !isService ? DC[ev.key] : null;
                const name = isService ? (CARDS[ev.key]?.title || ev.key) : (m ? m[3] : ev.key);
                const words = isService ? LABEL : DC_STATUS;
                const recovered = ev.to === 'good';
                return (
                  <div key={ev.id} className={`ss-log-row ${ev.to}`}>
                    <span className="ss-log-icon">{recovered ? '✓' : '!'}</span>
                    <div className="ss-log-body">
                      <div className="ss-log-line">
                        {!isService && m && <img src={flagUrl(m[1], 40)} alt="" className="ss-flag sm" loading="lazy" />}
                        <b>{name}</b>
                        {!isService && <span className="ss-log-tag">{t('steamStatus.logRegion', 'region')}</span>}
                      </div>
                      <div className="ss-log-change">
                        {ev.from ? <><span className={`ss-log-st ${ev.from}`}>{words[ev.from] || ev.from}</span> → </> : null}
                        <span className={`ss-log-st ${ev.to}`}>{words[ev.to] || ev.to}</span>
                        {ev.value && <span className="ss-raw">{ev.value}</span>}
                      </div>
                      <div className="ss-log-time">{formatLocalTime(ev.at, { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </aside>
    </div>
  );
}
